<template>
  <div
    class="custom-carousel"
    @mouseenter="stopAutoplay"
    @mouseleave="startAutoplay"
    @touchstart="handleTouchStart"
    @touchend="handleTouchEnd"
    aria-roledescription="carrossel"
    aria-label="Carrossel de Destaques"
  >
    <div class="carousel-inner-custom">
      <div
        v-for="(destaque, index) in destaques"
        :key="destaque.id"
        class="carousel-item-custom"
        :class="{ active: index === activeIndex }"
        :aria-hidden="index !== activeIndex"
      >
        <a
          :href="destaque.linkNoticia"
          :target="isExternal(destaque.linkNoticia) ? '_blank' : '_self'"
          rel="noopener noreferrer"
          class="slide-link"
        >
          <img
            :src="destaque.imagemUrl"
            :alt="destaque.descricaoImagem"
            class="slide-image"
            :fetchpriority="index === 0 ? 'high' : 'auto'"
            :loading="index === 0 ? 'eager' : 'lazy'"
            decoding="async"
          />
        </a>
      </div>
    </div>

    <!-- Navegação por setas -->
    <button
      class="nav-btn prev"
      @click="prevSlide"
      aria-label="Destaque anterior"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="15 18 9 12 15 6"></polyline>
      </svg>
    </button>
    <button
      class="nav-btn next"
      @click="nextSlide"
      aria-label="Próximo destaque"
    >
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="24"
        height="24"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
      >
        <polyline points="9 18 15 12 9 6"></polyline>
      </svg>
    </button>

    <!-- Indicadores (Dots) -->
    <div
      class="carousel-indicators-custom"
      role="tablist"
    >
      <button
        v-for="(_, index) in destaques"
        :key="index"
        class="indicator-dot"
        :class="{ active: index === activeIndex }"
        role="tab"
        :aria-selected="index === activeIndex"
        :aria-label="'Ir para o destaque ' + (index + 1)"
        @click="goToSlide(index)"
      ></button>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from "vue";

const props = defineProps({
  destaques: {
    type: Array,
    required: true,
    default: () => [],
  },
});

const activeIndex = ref(0);
let autoplayTimer = null;
let touchStartX = 0;
let touchEndX = 0;

function isExternal(url) {
  if (!url) return false;
  return url.startsWith("http://") || url.startsWith("https://");
}

function nextSlide() {
  if (props.destaques.length === 0) return;
  activeIndex.value = (activeIndex.value + 1) % props.destaques.length;
}

function prevSlide() {
  if (props.destaques.length === 0) return;
  activeIndex.value =
    (activeIndex.value - 1 + props.destaques.length) % props.destaques.length;
}

function goToSlide(index) {
  activeIndex.value = index;
}

function startAutoplay() {
  stopAutoplay();
  if (props.destaques.length > 1) {
    autoplayTimer = setInterval(nextSlide, 5000);
  }
}

function stopAutoplay() {
  if (autoplayTimer) {
    clearInterval(autoplayTimer);
    autoplayTimer = null;
  }
}

function handleTouchStart(event) {
  touchStartX = event.changedTouches[0].screenX;
}

function handleTouchEnd(event) {
  touchEndX = event.changedTouches[0].screenX;
  handleSwipe();
}

function handleSwipe() {
  const threshold = 50;
  if (touchStartX - touchEndX > threshold) {
    nextSlide(); // Swipe left -> Next
  } else if (touchEndX - touchStartX > threshold) {
    prevSlide(); // Swipe right -> Prev
  }
}

onMounted(() => {
  startAutoplay();
});

onUnmounted(() => {
  stopAutoplay();
});
</script>

<style scoped>
.custom-carousel {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 7;
  max-height: 550px;
  min-height: 350px;
  border-radius: 1rem;
  overflow: hidden;
  user-select: none;
  background-color: #f8f9fa;
}

.carousel-inner-custom {
  position: relative;
  width: 100%;
  height: 100%;
}

.carousel-item-custom {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  opacity: 0;
  transition: opacity 0.4s ease-in-out;
  z-index: 1;
}

.carousel-item-custom.active {
  opacity: 1;
  z-index: 3;
}

.slide-link {
  display: block;
  width: 100%;
  height: 100%;
  position: relative;
  text-decoration: none;
}

.slide-image {
  width: 100%;
  height: 100%;
  object-fit: contain;
  background-color: transparent;
}

/* Setas de navegação */
.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(26, 15, 62, 0.6);
  border: 1px solid rgba(255, 255, 255, 0.1);
  color: white;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  transition: all 0.2s ease;
  outline: none;
}

.nav-btn:hover {
  background: #7c3aed;
  transform: translateY(-50%) scale(1.05);
}

.nav-btn.prev {
  left: 20px;
}

.nav-btn.next {
  right: 20px;
}

/* Indicadores (Dots) */
.carousel-indicators-custom {
  position: absolute;
  bottom: 20px;
  left: 50%;
  transform: translateX(-50%);
  display: flex;
  gap: 8px;
  z-index: 10;
}

.indicator-dot {
  background: white;
  border: none;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  cursor: pointer;
  padding: 0;
  transition: all 0.25s ease;
  outline: none;
  box-shadow: 0 0px 6px rgba(0, 0, 0, 0.5);
}

.indicator-dot:hover {
  background: rgba(255, 255, 255, 0.8);
}

.indicator-dot.active {
  background: #7c3aed;
  width: 24px;
  border-radius: 5px;
}

/* Responsividade */
@media (max-width: 768px) {
  .custom-carousel {
    height: 300px;
  }
  .nav-btn {
    width: 36px;
    height: 36px;
  }
  .nav-btn.prev {
    left: 10px;
  }
  .nav-btn.next {
    right: 10px;
  }
}
</style>
